import { FacebookFeedPostOverlay } from '@src/components/stage/safe-zones/overlays/FacebookFeedPostOverlay';
import { InstagramFeedPostOverlay } from '@src/components/stage/safe-zones/overlays/InstagramFeedPostOverlay';
import { InstagramReelsOverlay } from '@src/components/stage/safe-zones/overlays/InstagramReelsOverlay';
import { LinkedInFeedPostOverlay } from '@src/components/stage/safe-zones/overlays/LinkedInFeedPostOverlay';
import { LinkedInShortsOverlay } from '@src/components/stage/safe-zones/overlays/LinkedInShortsOverlay';
import { ThreadsFeedPostOverlay } from '@src/components/stage/safe-zones/overlays/ThreadsFeedPostOverlay';
import { TikTokOverlay } from '@src/components/stage/safe-zones/overlays/TikTokOverlay';
import { XFeedPostOverlay } from '@src/components/stage/safe-zones/overlays/XFeedPostOverlay';
import { YouTubeLongFormVideoOverlay } from '@src/components/stage/safe-zones/overlays/YouTubeLongFormVideoOverlay';
import { YouTubeShortsOverlay } from '@src/components/stage/safe-zones/overlays/YouTubeShortsOverlay';
import { AspectRatio } from '@src/types.ts/video';

import { SAFE_ZONE_STRINGS } from './strings';
import {
  CHANNEL_LABELS,
  CHANNEL_ORDER,
  FORMAT_LABELS,
  FORMAT_ORDER,
  type SafeZoneChannel,
  type SafeZoneDefinition,
  type SafeZoneKey,
  type SafeZoneSelection,
} from './types';

const LINKEDIN_FEED_POST_COPY = SAFE_ZONE_STRINGS.overlays.linkedinFeedPost;
const LINKEDIN_SHORTS_COPY = SAFE_ZONE_STRINGS.overlays.linkedinShortFormVideo;
const YOUTUBE_LONG_FORM_COPY = SAFE_ZONE_STRINGS.overlays.youtubeLongFormVideo;
const YOUTUBE_SHORTS_COPY = SAFE_ZONE_STRINGS.overlays.youtubeShortFormVideo;
const INSTAGRAM_FEED_POST_COPY = SAFE_ZONE_STRINGS.overlays.instagramFeedPost;
const FACEBOOK_FEED_POST_COPY = SAFE_ZONE_STRINGS.overlays.facebookFeedPost;
const X_FEED_POST_COPY = SAFE_ZONE_STRINGS.overlays.xFeedPost;

/**
 * Registry of platform Safe Zone overlays. Empty until the per-platform
 * tickets land — the dropdown lists `None` plus every entry registered here.
 *
 * Per-platform tickets register their entry by editing this file. Mirror the
 * change in `docs/plans/PLAT-6294-creator-studio-safe-zones-for-video/safe-zone-config.md`.
 */
export const SAFE_ZONE_REGISTRY: Partial<
  Record<SafeZoneKey, SafeZoneDefinition>
> = {
  'linkedin.feed_post': {
    channel: 'linkedin',
    format: 'feed_post',
    key: 'linkedin.feed_post',
    channelLabel: CHANNEL_LABELS.linkedin,
    formatLabel: FORMAT_LABELS.feed_post,
    supportedAspectRatios: [
      AspectRatio.AR_16_9,
      AspectRatio.AR_1_1,
      AspectRatio.AR_9_16,
    ],
    variants: ['showPlayControls', 'showCaptions'],
    variantHints: {
      showPlayControls: LINKEDIN_FEED_POST_COPY.controlsHint,
      showCaptions: LINKEDIN_FEED_POST_COPY.captionsHint,
    },
    defaultVariants: { showPlayControls: true, showCaptions: true },
    Component: LinkedInFeedPostOverlay,
  },
  'linkedin.short_form_video': {
    channel: 'linkedin',
    format: 'short_form_video',
    key: 'linkedin.short_form_video',
    channelLabel: CHANNEL_LABELS.linkedin,
    formatLabel: FORMAT_LABELS.short_form_video,
    // 9:16 only for now. LinkedIn does accept 1:1 uploads on the shorts
    // surface (centered with letterbox bands), but support is deferred —
    // the synthetic-viewport plumbing was removed when 1:1 was pulled
    // from scope.
    supportedAspectRatios: [AspectRatio.AR_9_16],
    // No player-controls bar variant — LinkedIn's shorts UI doesn't have
    // one. The overlay exposes only the captions toggle; a thin always-on
    // scrubber line lives at the bottom of the viewport.
    variants: ['showCaptions'],
    variantHints: {
      showCaptions: LINKEDIN_SHORTS_COPY.captionsHint,
    },
    defaultVariants: { showCaptions: true },
    Component: LinkedInShortsOverlay,
  },
  'x.feed_post': {
    channel: 'x',
    format: 'feed_post',
    key: 'x.feed_post',
    channelLabel: CHANNEL_LABELS.x,
    formatLabel: FORMAT_LABELS.feed_post,
    // X plays all three card shapes in feed with the same in-card player
    // chrome — the bottom controls bar paints identically across aspect
    // ratios; only the caption block scales its type and wrap behavior.
    supportedAspectRatios: [
      AspectRatio.AR_16_9,
      AspectRatio.AR_1_1,
      AspectRatio.AR_9_16,
    ],
    variants: ['showPlayControls', 'showCaptions'],
    variantHints: {
      showPlayControls: X_FEED_POST_COPY.controlsHint,
      showCaptions: X_FEED_POST_COPY.captionsHint,
    },
    defaultVariants: { showPlayControls: true, showCaptions: true },
    Component: XFeedPostOverlay,
  },
  'tiktok.short_form_video': {
    channel: 'tiktok',
    format: 'short_form_video',
    key: 'tiktok.short_form_video',
    channelLabel: CHANNEL_LABELS.tiktok,
    formatLabel: FORMAT_LABELS.short_form_video,
    // 9:16 only. TikTok does accept square uploads (centered with letterbox
    // bands on the real surface), but Phase 1 is 9:16-only across all
    // Shorts overlays — synthetic-viewport plumbing for letterboxed
    // squares was removed during PLAT-9824.
    supportedAspectRatios: [AspectRatio.AR_9_16],
    // No `showCaptions` variant — TikTok has no auto-captions on the
    // viewer side. Whatever captions a creator bakes into the video are
    // what plays; there's no platform-level CC toggle for viewers, so
    // there is nothing for the preview to simulate the on/off state of.
    // No `showPlayControls` either — TikTok's For You viewer has no
    // persistent controls bar; the bottom scrubber line is the only
    // playback affordance.
    Component: TikTokOverlay,
  },
  'instagram.feed_post': {
    channel: 'instagram',
    format: 'feed_post',
    key: 'instagram.feed_post',
    channelLabel: CHANNEL_LABELS.instagram,
    formatLabel: FORMAT_LABELS.feed_post,
    // 1:1 (square) and 9:16 (vertical) — both render in Instagram's main
    // feed (vertical feed video is shown at native 9:16, clipped to the
    // 4:5 max display ratio in production but rendered tall in the
    // safe-zone preview). 16:9 landscape is letterboxed inside a square
    // feed card on the real surface and isn't a useful safe-zones target,
    // so it's out of scope. 4:5 portrait is also accepted by Instagram but
    // the project's `AspectRatio` enum doesn't expose it. The full-bleed
    // vertical Reels surface is a separate registration
    // (`instagram.short_form_video`).
    supportedAspectRatios: [AspectRatio.AR_1_1, AspectRatio.AR_9_16],
    // No `showCaptions` variant — Instagram doesn't paint a viewer-side
    // captions toggle on feed-post videos, so there's no on/off state for
    // the preview to simulate. The `showPlayControls` toggle gates the
    // entire surrounding feed chrome (top profile row, bottom action row +
    // caption preview, mute speaker).
    variants: ['showPlayControls'],
    variantHints: {
      showPlayControls: INSTAGRAM_FEED_POST_COPY.controlsHint,
    },
    defaultVariants: { showPlayControls: true },
    Component: InstagramFeedPostOverlay,
  },
  'instagram.short_form_video': {
    channel: 'instagram',
    format: 'short_form_video',
    key: 'instagram.short_form_video',
    channelLabel: CHANNEL_LABELS.instagram,
    formatLabel: FORMAT_LABELS.short_form_video,
    // 9:16 only — Reels is vertical-only on the real surface; non-9:16
    // uploads are rejected at the platform layer.
    supportedAspectRatios: [AspectRatio.AR_9_16],
    // No variants — Reels has no viewer-side captions toggle (whatever
    // captions the creator bakes into the video play as-is) and no
    // persistent player-controls bar (the thin bottom progress line is the
    // only playback affordance). Same posture as TikTok and Threads.
    Component: InstagramReelsOverlay,
  },
  'facebook.feed_post': {
    channel: 'facebook',
    format: 'feed_post',
    key: 'facebook.feed_post',
    channelLabel: CHANNEL_LABELS.facebook,
    formatLabel: FORMAT_LABELS.feed_post,
    // 16:9 (landscape) and 1:1 (square) — both render natively in the
    // Facebook feed. 9:16 vertical is also accepted on the real surface
    // but renders inside a letterboxed 4:5 card, so it's not a useful
    // safe-zones target; the full-bleed vertical Reels surface would be
    // a separate registration (`facebook.short_form_video`).
    supportedAspectRatios: [AspectRatio.AR_16_9, AspectRatio.AR_1_1],
    variants: ['showPlayControls', 'showCaptions'],
    variantHints: {
      showPlayControls: FACEBOOK_FEED_POST_COPY.controlsHint,
      showCaptions: FACEBOOK_FEED_POST_COPY.captionsHint,
    },
    defaultVariants: { showPlayControls: true, showCaptions: true },
    Component: FacebookFeedPostOverlay,
  },
  'threads.feed_post': {
    channel: 'threads',
    format: 'feed_post',
    key: 'threads.feed_post',
    channelLabel: CHANNEL_LABELS.threads,
    formatLabel: FORMAT_LABELS.feed_post,
    // Threads accepts portrait (9:16), square (1:1), and landscape (16:9)
    // video in feed posts; the one piece of chrome the overlay paints (a
    // mute speaker in the bottom-right corner) renders identically at all
    // three.
    supportedAspectRatios: [
      AspectRatio.AR_16_9,
      AspectRatio.AR_1_1,
      AspectRatio.AR_9_16,
    ],
    // No variants — Threads has no automatic / burned-in captions on the
    // video itself, and the play / scrubber / time chrome lives outside the
    // video card in the surrounding feed UI. The mute button is always
    // painted.
    Component: ThreadsFeedPostOverlay,
  },
  'youtube.long_form_video': {
    channel: 'youtube',
    format: 'long_form_video',
    key: 'youtube.long_form_video',
    channelLabel: CHANNEL_LABELS.youtube,
    formatLabel: FORMAT_LABELS.long_form_video,
    // YouTube's standard watch-page player is 16:9. Shorts (9:16) is a
    // separate registration (`youtube.short_form_video`).
    supportedAspectRatios: [AspectRatio.AR_16_9],
    variants: ['showPlayControls', 'showCaptions'],
    variantHints: {
      showPlayControls: YOUTUBE_LONG_FORM_COPY.controlsHint,
      showCaptions: YOUTUBE_LONG_FORM_COPY.captionsHint,
    },
    defaultVariants: { showPlayControls: true, showCaptions: true },
    Component: YouTubeLongFormVideoOverlay,
  },
  'youtube.short_form_video': {
    channel: 'youtube',
    format: 'short_form_video',
    key: 'youtube.short_form_video',
    channelLabel: CHANNEL_LABELS.youtube,
    formatLabel: FORMAT_LABELS.short_form_video,
    // 9:16 only — YouTube Shorts is vertical-only on the real surface;
    // non-9:16 uploads are rejected at the platform layer.
    supportedAspectRatios: [AspectRatio.AR_9_16],
    // No player-controls bar variant — Shorts has no persistent controls
    // bar. Captions toggle via the toolbar pill; a thin always-on red
    // scrubber line lives at the bottom of the viewport (matches the
    // LinkedIn / Instagram Shorts pattern).
    variants: ['showCaptions'],
    variantHints: {
      showCaptions: YOUTUBE_SHORTS_COPY.captionsHint,
    },
    defaultVariants: { showCaptions: true },
    Component: YouTubeShortsOverlay,
  },
};

/**
 * Group registered definitions by channel for the dropdown's
 * channel-headered layout. Channels with no registered formats are omitted.
 * Stable order: `CHANNEL_ORDER` × `FORMAT_ORDER`.
 */
export function groupByChannel(): Array<{
  channel: SafeZoneChannel;
  channelLabel: string;
  definitions: SafeZoneDefinition[];
}> {
  const groups: Array<{
    channel: SafeZoneChannel;
    channelLabel: string;
    definitions: SafeZoneDefinition[];
  }> = [];
  for (const channel of CHANNEL_ORDER) {
    const definitions: SafeZoneDefinition[] = [];
    for (const format of FORMAT_ORDER) {
      const def = SAFE_ZONE_REGISTRY[`${channel}.${format}`];
      if (def) definitions.push(def);
    }
    if (definitions.length > 0) {
      groups.push({
        channel,
        channelLabel: definitions[0].channelLabel,
        definitions,
      });
    }
  }
  return groups;
}

/** All registered definitions in stable (channel × format) order. */
export function listRegisteredDefinitions(): SafeZoneDefinition[] {
  return groupByChannel().flatMap(g => g.definitions);
}

/**
 * Whether an overlay is designed for the given aspect ratio. `'none'` fits any.
 * Shared so every entry point applies the same rule — the dropdown, the session
 * store's AR-change fallback, and the `?clipSafeZone=` restore (PLAT-10948).
 */
export function safeZoneSupportsAspectRatio(
  selection: SafeZoneSelection,
  aspectRatio: string,
): boolean {
  if (selection === 'none') return true;
  const def = SAFE_ZONE_REGISTRY[selection];
  return def?.supportedAspectRatios.some(ar => ar === aspectRatio) ?? false;
}
